import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
    RefreshCw, Calendar, User, StopCircle, ChevronRight, Clock, 
    AlertCircle, Repeat, ArrowLeft
} from 'lucide-react';

const RECURRENCE_LABELS = {
    daily: 'Täglich',
    every_2_days: 'Alle 2 Tage',
    every_3_days: 'Alle 3 Tage',
    every_4_days: 'Alle 4 Tage',
    every_5_days: 'Alle 5 Tage',
    weekly: 'Wöchentlich',
    biweekly: 'Alle 2 Wochen',
    monthly: 'Monatlich',
    quarterly: 'Vierteljährlich',
    yearly: 'Jährlich'
};

const RecurringTasksPage = () => {
    const { api } = useAuth();
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stoppingTask, setStoppingTask] = useState(null);
    const [showStopModal, setShowStopModal] = useState(false);

    useEffect(() => {
        fetchRecurringTasks();
    }, []);

    const fetchRecurringTasks = async () => {
        try {
            const response = await api.get('/tasks/recurring');
            setTasks(response.data);
        } catch (error) {
            console.error('Failed to fetch recurring tasks:', error);
            toast.error('Fehler beim Laden der wiederkehrenden Aufgaben');
        } finally {
            setLoading(false);
        }
    };

    const handleStopRecurring = async () => {
        if (!stoppingTask) return;
        
        try {
            await api.delete(`/tasks/${stoppingTask.id}/stop-recurring`);
            toast.success('Wiederholung beendet');
            setTasks(tasks.filter(t => t.id !== stoppingTask.id));
        } catch (error) {
            toast.error('Fehler beim Beenden der Wiederholung');
        } finally {
            setShowStopModal(false);
            setStoppingTask(null);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'todo': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
            case 'in_progress': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
            case 'review': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
            default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return 'bg-red-500/20 text-red-400 border-red-500/30';
            case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
            case 'low': return 'bg-green-500/20 text-green-400 border-green-500/30';
            default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <Button 
                        variant="ghost" 
                        className="mb-4 gap-2"
                        onClick={() => navigate('/tasks')}
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Zurück zu Tasks
                    </Button>
                    
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                            <Repeat className="w-7 h-7 text-primary" />
                        </div>
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
                                Wiederkehrende Aufgaben
                            </h1>
                            <p className="text-muted-foreground mt-1">
                                {tasks.length} aktive Wiederholungen
                            </p>
                        </div>
                    </div>
                </motion.div>

                {/* Tasks List */}
                {tasks.length === 0 ? (
                    <Card className="border-dashed">
                        <CardContent className="py-16 text-center">
                            <RefreshCw className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                            <h3 className="text-lg font-medium mb-2">Keine wiederkehrenden Aufgaben</h3>
                            <p className="text-muted-foreground text-sm">
                                Erstelle eine Aufgabe mit Wiederholung, um sie hier zu sehen.
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-3">
                        {tasks.map((task, index) => (
                            <motion.div
                                key={task.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                            >
                                <Card className="hover:shadow-lg transition-all cursor-pointer group">
                                    <CardContent className="p-4">
                                        <div className="flex items-start justify-between gap-4">
                                            {/* Left: Task Info */}
                                            <div 
                                                className="flex-1 min-w-0"
                                                onClick={() => navigate(`/tasks?active=${task.id}`)}
                                            >
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Badge className={`${getPriorityColor(task.priority)} text-xs`}>
                                                        {task.priority === 'high' ? 'Hoch' : task.priority === 'medium' ? 'Mittel' : 'Niedrig'}
                                                    </Badge>
                                                    <Badge className={`${getStatusColor(task.status)} text-xs`}>
                                                        {task.status === 'todo' ? 'Offen' : task.status === 'in_progress' ? 'In Arbeit' : 'Review'}
                                                    </Badge>
                                                </div>
                                                
                                                <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                                                    {task.title}
                                                </h3>
                                                
                                                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                                                    <span className="flex items-center gap-1.5 text-primary font-medium">
                                                        <Repeat className="w-3.5 h-3.5" />
                                                        {RECURRENCE_LABELS[task.recurrence_pattern] || task.recurrence_pattern}
                                                    </span>
                                                    
                                                    {task.deadline && (
                                                        <span className="flex items-center gap-1.5">
                                                            <Calendar className="w-3.5 h-3.5" />
                                                            Nächste: {formatDate(task.deadline)}
                                                        </span>
                                                    )}
                                                    
                                                    {task.project_name && (
                                                        <span className="truncate max-w-[150px]">
                                                            {task.project_name}
                                                        </span>
                                                    )}
                                                </div>
                                                
                                                {/* Assigned Users */}
                                                {task.assigned_users && task.assigned_users.length > 0 && (
                                                    <div className="mt-3 flex items-center gap-2">
                                                        <span className="text-xs text-muted-foreground">Zugewiesen:</span>
                                                        <div className="flex -space-x-2">
                                                            {task.assigned_users.slice(0, 3).map((user) => (
                                                                <Avatar key={user.id} className="w-6 h-6 border-2 border-background">
                                                                    <AvatarFallback 
                                                                        style={{ backgroundColor: user.profile_color || '#009dde' }}
                                                                        className="text-white text-[10px]"
                                                                    >
                                                                        {user.first_name?.[0]}{user.last_name?.[0]}
                                                                    </AvatarFallback>
                                                                </Avatar>
                                                            ))}
                                                            {task.assigned_users.length > 3 && (
                                                                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] border-2 border-background">
                                                                    +{task.assigned_users.length - 3}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* Right: Actions */}
                                            <div className="flex items-center gap-2 shrink-0">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setStoppingTask(task);
                                                        setShowStopModal(true);
                                                    }}
                                                >
                                                    <StopCircle className="w-4 h-4 mr-1" />
                                                    <span className="hidden sm:inline">Stoppen</span>
                                                </Button>
                                                <ChevronRight className="w-5 h-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                )}

                {/* Stop Confirmation Modal */}
                <Dialog open={showStopModal} onOpenChange={setShowStopModal}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <AlertCircle className="w-5 h-5 text-destructive" />
                                Wiederholung beenden?
                            </DialogTitle>
                            <DialogDescription>
                                Die Wiederholung für "{stoppingTask?.title}" wird gestoppt. 
                                Der aktuelle Task bleibt bestehen, aber es werden keine neuen Instanzen mehr erstellt.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter className="gap-2">
                            <Button variant="outline" onClick={() => setShowStopModal(false)}>
                                Abbrechen
                            </Button>
                            <Button variant="destructive" onClick={handleStopRecurring}>
                                Wiederholung beenden
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
};

export default RecurringTasksPage;
